import React, { useState, useEffect, useMemo } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";

// 12-hour AM/PM slots from 10:00 AM to 09:30 PM in strict 30-min steps
const SLOTS = (() => {
  const out = [];
  for (let h = 10; h <= 21; h++) {
    for (const m of [0, 30]) {
      const value = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
      const meridiem = h < 12 ? "AM" : "PM";
      const hh12 = h === 12 ? 12 : h > 12 ? h - 12 : h;
      out.push({ value, label: `${hh12}:${String(m).padStart(2, "0")} ${meridiem}` });
    }
  }
  return out;
})();

export default function BookingModal({ open, onClose, services }) {
  const [form, setForm] = useState({
    name: "", phone: "", email: "", service: "", date: "", time: "10:00", notes: "", staff_id: "",
  });
  const [loading, setLoading] = useState(false);
  const [staff, setStaff] = useState([]);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [serviceSearch, setServiceSearch] = useState("");

  useEffect(() => {
    if (!open) return;
    api.get("/public/staff").then(r => setStaff(r.data || [])).catch(() => setStaff([]));
  }, [open]);

  useEffect(() => {
    if (!form.staff_id || !form.date) { setBookedSlots([]); return; }
    api.get(`/public/staff/${form.staff_id}/availability?date=${form.date}`)
      .then(r => setBookedSlots(r.data?.booked_slots || []))
      .catch(() => setBookedSlots([]));
  }, [form.staff_id, form.date]);

  // Group services by category for the dropdown
  const grouped = useMemo(() => {
    const map = {};
    (services || []).forEach(s => {
      const cat = s.category || "Other";
      if (!map[cat]) map[cat] = [];
      map[cat].push(s);
    });
    return map;
  }, [services]);

  if (!open) return null;

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.service || !form.date) {
      toast.error("Please fill all required fields");
      return;
    }
    // Validate UAE phone format: must start with 050, 052, 054, 055, 056, 058 (or international +971...)
    const digits = form.phone.replace(/\D/g, "");
    const validLocal = /^05[02456 8]\d{7}$/.test(digits);
    const validIntl = /^9715[02456 8]\d{7}$/.test(digits);
    if (!validLocal && !validIntl) {
      toast.error("Please enter a valid UAE mobile (e.g. 0501234567)");
      return;
    }
    if (form.staff_id && bookedSlots.includes(form.time)) {
      toast.error("That slot is already taken with this staff member. Please pick another time.");
      return;
    }
    setLoading(true);
    try {
      // Send the phone exactly as the user typed it (server stores it as-is).
      const payload = { ...form, staff_id: form.staff_id || undefined };
      await api.post("/bookings", payload);
      toast.success("Booking request received! We'll confirm shortly via WhatsApp.");
      onClose();
      setForm({ name: "", phone: "", email: "", service: "", date: "", time: "10:00", notes: "", staff_id: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not submit. Please try again or WhatsApp us directly.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const minDate = new Date().toISOString().split("T")[0];

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-3 sm:p-4" data-testid="booking-modal">
      <div className="bg-white max-w-md w-full max-h-[92vh] overflow-y-auto border border-[var(--border)]">
        <div className="px-4 sm:px-5 py-3 sm:py-3.5 border-b border-[var(--border)] flex items-center justify-between bg-[var(--bg)] sticky top-0 z-10">
          <h3 className="font-display text-base sm:text-lg md:text-xl">Book an Appointment</h3>
          <button onClick={onClose} aria-label="Close" data-testid="booking-close" className="text-xl leading-none">×</button>
        </div>
        <form onSubmit={submit} className="p-4 sm:p-5 space-y-2.5 sm:space-y-3">
          <Field label="Full Name *">
            <input value={form.name} onChange={e=>update("name",e.target.value)} required data-testid="booking-name"
              className="w-full bg-[var(--bg)] border border-[var(--border)] px-3 py-1.5 sm:py-2 text-[13px] sm:text-sm focus:outline-none focus:border-[var(--accent)]" />
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-3">
            <Field label="Phone / WhatsApp *">
              <input value={form.phone} onChange={e=>update("phone",e.target.value)} required data-testid="booking-phone"
                placeholder="e.g. 0501234567" maxLength={12}
                className="w-full bg-[var(--bg)] border border-[var(--border)] px-3 py-1.5 sm:py-2 text-[13px] sm:text-sm focus:outline-none focus:border-[var(--accent)]" />
            </Field>
            <Field label="Email">
              <input type="email" value={form.email} onChange={e=>update("email",e.target.value)} data-testid="booking-email"
                className="w-full bg-[var(--bg)] border border-[var(--border)] px-3 py-1.5 sm:py-2 text-[13px] sm:text-sm focus:outline-none focus:border-[var(--accent)]" />
            </Field>
          </div>
          <Field label="Service *">
            <select value={form.service} onChange={e=>update("service",e.target.value)} required data-testid="booking-service"
              className="w-full bg-[var(--bg)] border border-[var(--border)] px-3 py-1.5 sm:py-2 text-[13px] sm:text-sm focus:outline-none focus:border-[var(--accent)]">
              <option value="">Select a service</option>
              {Object.keys(grouped).sort().map(cat => (
                <optgroup key={cat} label={cat}>
                  {grouped[cat].map(s => (
                    <option key={s.id} value={s.name}>{s.name} — AED {s.price_aed}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </Field>
          <Field label="Preferred Staff (optional)">
            <select value={form.staff_id} onChange={e=>update("staff_id",e.target.value)} data-testid="booking-staff"
              className="w-full bg-[var(--bg)] border border-[var(--border)] px-3 py-1.5 sm:py-2 text-[13px] sm:text-sm focus:outline-none focus:border-[var(--accent)]">
              <option value="">Any available staff</option>
              {staff.map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-2.5 sm:gap-3">
            <Field label="Date *">
              <input type="date" min={minDate} value={form.date} onChange={e=>update("date",e.target.value)} required data-testid="booking-date"
                className="w-full bg-[var(--bg)] border border-[var(--border)] px-3 py-1.5 sm:py-2 text-[13px] sm:text-sm focus:outline-none focus:border-[var(--accent)]" />
            </Field>
            <Field label="Time *">
              <select value={form.time} onChange={e=>update("time",e.target.value)} required data-testid="booking-time"
                className="w-full bg-[var(--bg)] border border-[var(--border)] px-3 py-1.5 sm:py-2 text-[13px] sm:text-sm focus:outline-none focus:border-[var(--accent)]">
                {SLOTS.map(s => {
                  const taken = form.staff_id && bookedSlots.includes(s.value);
                  return (
                    <option key={s.value} value={s.value} disabled={taken}>
                      {s.label}{taken ? " — booked" : ""}
                    </option>
                  );
                })}
              </select>
            </Field>
          </div>
          {form.staff_id && bookedSlots.length > 0 && (
            <p className="text-[10px] sm:text-[11px] text-[var(--muted)]">Slots in 30-min intervals. Greyed slots are already booked with this staff.</p>
          )}
          <Field label="Notes (optional)">
            <textarea value={form.notes} onChange={e=>update("notes",e.target.value)} rows={2} data-testid="booking-notes"
              className="w-full bg-[var(--bg)] border border-[var(--border)] px-3 py-1.5 sm:py-2 text-[13px] sm:text-sm focus:outline-none focus:border-[var(--accent)]" />
          </Field>
          <p className="text-[10px] sm:text-[11px] text-[var(--muted)] italic">All booking requests require confirmation. We&apos;ll reach out via WhatsApp once approved.</p>
          <button type="submit" disabled={loading}
            className="btn-gold w-full rounded-full py-2 sm:py-2.5 text-[11px] sm:text-xs tracking-widest uppercase disabled:opacity-60" data-testid="booking-submit">
            {loading ? "Sending..." : "Request Appointment"}
          </button>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[9px] sm:text-[10px] uppercase tracking-widest text-[var(--muted)] mb-1 block">{label}</span>
      {children}
    </label>
  );
}
